import { timingInstants, Temporal, type DisplayItem, type Timing } from '@autoday/core';
import { formatDateShort, formatTime } from '../i18n/format.ts';
import { tr } from '../i18n/i18n.ts';
import { PALETTES, type PaletteId } from './palettes.ts';

export function timeLabel(t: Timing, tz: string): string {
  if (t.kind === 'allDay') return tr('Heldag', 'All day');
  const { start, end } = timingInstants(t, tz);
  const s = formatTime(start, tz);
  const e = formatTime(end, tz);
  const sd = start.toZonedDateTimeISO(tz).toPlainDate();
  const ed = end.toZonedDateTimeISO(tz).toPlainDate();
  if (!sd.equals(ed)) return `${s}–${e} (${formatDateShort(ed.toString())})`;
  return `${s}–${e}`;
}

export function itemAriaLabel(i: DisplayItem, tz: string, calendarLabel: string = i.calendarName): string {
  const parts = [i.sourceSummary ?? i.title, timeLabel(i.timing, tz), calendarLabel];
  if (i.locked) parts.push(tr('importerat källdatum, kan inte flyttas', 'imported source date, cannot be moved'));
  if (i.done) parts.push(tr('klar här', 'done here'));
  if (i.recurring) parts.push(tr('återkommande', 'recurring'));
  return parts.join(', ');
}

export function relativeFromNow(iso: string, now: Temporal.Instant): string {
  const d = Temporal.Instant.from(iso).since(now, { largestUnit: 'hours' });
  const mins = Math.round(d.total({ unit: 'minutes' }));
  if (Math.abs(mins) < 1) return tr('nu', 'now');
  const abs = Math.abs(mins);
  const txt = abs < 60 ? `${abs} min` : abs < 48 * 60 ? `${Math.round(abs / 60)} h` : tr(`${Math.round(abs / 1440)} dagar`, `${Math.round(abs / 1440)} days`);
  return mins > 0 ? tr(`om ${txt}`, `in ${txt}`) : tr(`för ${txt} sedan`, `${txt} ago`);
}
/**
 * Kalenderns färg (användardata, oftast en ljus pastell) anpassad till aktivt tema: oförändrad i mörkt
 * tema, mörkad i ljust (--cat-keep i styles.css) så att symboler och kanter håller minst 3:1.
 * Bara #rgb/#rrggbb godtas; annat ger temats neutrala kantfärg.
 */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
export function catColor(color: string | undefined): string {
  return color && HEX.test(color) ? `color-mix(in srgb, ${color} var(--cat-keep), #000)` : 'var(--color-border-strong)';
}

/**
 * Händelsefärg för FullCalendar (som bara godtar vanliga färgvärden, inte color-mix/var). Mörkt läge:
 * palettens neutrala yta (Original: samma som tidigare). Ljust: kategorifärgen mörkad med samma faktor
 * som --cat-keep (55 %); forma-temat blandar den själv till en lätt nyans och ritar kantstrecket i full färg.
 * Kategorifärgen är användardata och ändras aldrig av temat eller paletten.
 */
export function eventColors(color: string | undefined, theme: 'light' | 'dark', palette: PaletteId = 'original'): { background: string; text: string } {
  const p = PALETTES[palette] ?? PALETTES.original;
  if (theme === 'dark') return { background: p.dark.raised.toUpperCase(), text: p.dark.text.toUpperCase() };
  if (!color || !HEX.test(color)) return { background: p.light.borderStrong.toUpperCase(), text: '#FFFFFF' };
  const full = color.length === 4 ? '#' + [...color.slice(1)].map((c) => c + c).join('') : color;
  const shade = [1, 3, 5].map((i) => Math.round(parseInt(full.slice(i, i + 2), 16) * 0.55).toString(16).padStart(2, '0')).join('');
  return { background: '#' + shade.toUpperCase(), text: '#FFFFFF' };
}
